/**
 * Tracks DeviceFunctionCalls the client has initiated but not yet seen confirmed.
 *
 * Lifecycle per entry (keyed by dfc_id):
 * - Added when `callNamedDeviceActionAsync.fulfilled` returns a dfc_id.
 * - `confirmedAt` is set when a subsequent `loadDeviceActivityAsync` response
 *   reports the id in `confirmedDfcIds` (the device produced an event carrying
 *   this dfc_id).
 * - `timedOut` is set when the client-side deadline passes without confirmation.
 * - Confirmed and timed-out entries linger briefly so the UI can show the
 *   final state, then are cleared by `clearDfc` (dispatched from a timer).
 */
import type { Reducer } from "@reduxjs/toolkit"
import { createSelector, createSlice } from "@reduxjs/toolkit"

import {
  callNamedDeviceActionAsync,
  loadDeviceActivityAsync,
} from "@/base/farmhq-api"

import type { RootState } from "./root.reducer"

/** Timeout for a typical device function call. */
export const DEFAULT_DFC_TIMEOUT_MS = 15_000
/** Longer timeout for center pivot commands — actuation takes longer. */
export const CENTER_PIVOT_DFC_TIMEOUT_MS = 60_000
/**
 * Prefix shared by all center pivot named device actions (e.g. "cp_controller_set").
 * The response payload carries this in `namedDeviceAction.name`, not `functionName`
 * (which is always "call_device_commands" for JSON-protocol devices).
 */
export const CENTER_PIVOT_DFC_ACTION_NAME = "cp_controller_"

export interface PendingDfcEntry {
  dfcId: number
  deviceId: string
  actionName: string | null
  /** Epoch ms when the call was issued. */
  dispatchedAt: number
  /** ms until we treat the call as timed out without device confirmation. */
  timeoutMs: number
  /** Epoch ms when device confirmation arrived. */
  confirmedAt: number | null
  /** True once the client-side timeout has elapsed without confirmation. */
  timedOut: boolean
}

interface PendingDfcState {
  entries: Record<number, PendingDfcEntry>
}

const initialState: PendingDfcState = { entries: {} }

export function getDfcTimeoutMs(actionName: string | null | undefined): number {
  if (actionName?.startsWith(CENTER_PIVOT_DFC_ACTION_NAME)) {
    return CENTER_PIVOT_DFC_TIMEOUT_MS
  }
  return DEFAULT_DFC_TIMEOUT_MS
}

const slice = createSlice({
  name: "pendingDeviceFunctionCalls",
  initialState,
  reducers: {
    addPendingDfc: (
      state,
      action: {
        payload: {
          dfcId: number
          deviceId: string
          actionName: string | null
          timeoutMs: number
        }
      },
    ) => {
      const { dfcId, deviceId, actionName, timeoutMs } = action.payload
      state.entries[dfcId] = {
        dfcId,
        deviceId,
        actionName,
        dispatchedAt: Date.now(),
        timeoutMs,
        confirmedAt: null,
        timedOut: false,
      }
    },
    markDfcTimedOut: (state, action: { payload: { dfcId: number } }) => {
      const entry = state.entries[action.payload.dfcId]
      if (entry && entry.confirmedAt == null) {
        entry.timedOut = true
      }
    },
    clearDfc: (state, action: { payload: { dfcId: number } }) => {
      delete state.entries[action.payload.dfcId]
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(
        callNamedDeviceActionAsync.fulfilled,
        (state, { meta, payload }) => {
          const dfcId = payload?.result?.id
          if (dfcId == null) return
          // Prefer the named-action name (e.g. "cp_controller_set") over the
          // Particle function name ("call_device_commands"), since the latter is
          // the same for all JSON-protocol commands and carries no useful semantics.
          const actionName =
            payload.result?.namedDeviceAction?.name ??
            payload.result?.functionName ??
            null
          state.entries[dfcId] = {
            dfcId,
            deviceId: meta.arg.deviceId,
            actionName,
            dispatchedAt: Date.now(),
            timeoutMs: getDfcTimeoutMs(actionName),
            confirmedAt: null,
            timedOut: false,
          }
        },
      )
      .addCase(loadDeviceActivityAsync.fulfilled, (state, { payload }) => {
        const confirmed = payload.confirmedDfcIds ?? []
        const now = Date.now()
        for (const dfcId of confirmed) {
          const entry = state.entries[dfcId]
          if (entry && entry.confirmedAt == null) {
            entry.confirmedAt = now
          }
        }
      }),
})

export const { addPendingDfc, markDfcTimedOut, clearDfc } = slice.actions
const pendingDeviceFunctionCalls: Reducer<PendingDfcState> = slice.reducer
export default pendingDeviceFunctionCalls

// ---------- Selectors ----------

const getPendingDfcEntriesMap = (state: RootState) =>
  state.pendingDeviceFunctionCalls.entries

export const getAllPendingDfcEntries = createSelector(
  [getPendingDfcEntriesMap],
  (entries): PendingDfcEntry[] => Object.values(entries),
)

/** DFC IDs still awaiting device confirmation (not confirmed, not timed out). */
export const getWaitingDfcIds = createSelector(
  [getPendingDfcEntriesMap],
  (entries): number[] => {
    const ids: number[] = []
    for (const entry of Object.values(entries)) {
      if (entry.confirmedAt == null && !entry.timedOut) {
        ids.push(entry.dfcId)
      }
    }
    return ids
  },
)

/** DFCs awaiting confirmation for a specific device. */
export function getWaitingDfcsForDevice(
  state: RootState,
  deviceId: string,
): PendingDfcEntry[] {
  const result: PendingDfcEntry[] = []
  for (const entry of Object.values(state.pendingDeviceFunctionCalls.entries)) {
    if (
      entry.deviceId === deviceId &&
      entry.confirmedAt == null &&
      !entry.timedOut
    ) {
      result.push(entry)
    }
  }
  return result
}

export function getLatestDfcForDevice(
  state: RootState,
  deviceId: string,
): PendingDfcEntry | null {
  let latest: PendingDfcEntry | null = null
  for (const entry of Object.values(state.pendingDeviceFunctionCalls.entries)) {
    if (entry.deviceId !== deviceId) continue
    if (latest == null || entry.dispatchedAt > latest.dispatchedAt) {
      latest = entry
    }
  }
  return latest
}
